import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { ActivatedRoute } from "@angular/router";
import { DataType, IDateInterval, IFilter, IPreferenceUser, ITableParameter } from "@comunicare/comunicare-interfaces";
import * as moment from "moment";
import { Subject, Subscription, merge } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";
import { UserStatisticsApiService } from "src/app/providers/api/userStatistics-api.service";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { OrganizationsService } from "src/app/providers/organizations.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";
import { StatEventsDataSource } from "./statEvents.datasource";

@Component({
  selector: "app-stat-events-page",
  templateUrl: "./stat-events-page.component.html",
  styleUrls: ["./stat-events-page.component.scss"],
})
export class StatEventsPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  public isLoading = true;
  public eventsDataSource: StatEventsDataSource;
  public displayedColumns: string[] = ["eventDatetime", "eventSource", "description", "careplans", "userName", "services", "patientName"];
  public filters: IFilter[] = [];
  /** Raw active filters, kept in sync to feed `app-item-filter` via the `filterFor` pipe. */
  public activeFilters: IFilter[] = [];
  public isFiltered = false;
  public dataTypeText = DataType.TEXT;
  public dataTypeDate = DataType.DATE;
  public dataTypeChoice = DataType.CHOICE;
  public eventsCount = 0;
  public readonly initialFromDate: moment.Moment = moment().subtract(3, "months").startOf("day");
  public readonly initialToDate: moment.Moment = moment().endOf("day");
  private preferences: ITableParameter;
  public currentPageSize: number;
  private healthcareServicesRefs: string[];
  private includePrivateLogs: boolean;
  private currentDateInterval: IDateInterval;
  /** Subject that emits when the component has been destroyed. */
  private onDestroy$ = new Subject<void>();
  private loadSubscription?: Subscription;

  constructor(
    private sessionService: SessionService,
    private userService: UserService,
    private apiStatService: UserStatisticsApiService,
    private preferenceService: PreferenceService,
    private route: ActivatedRoute,
    private healthcareService: HealthcareserviceService,
    private organisationService: OrganizationsService
  ) {
    // get data from resolvers
    this.preferences = this.route.snapshot.data.preferences;
    const savedFilters = this.preferences?.filters ? this.preferences.filters.filter((f) => f.propertyName !== "eventDatetime") : [];
    this.currentPageSize = this.preferences?.itemsPerPage ? this.preferences.itemsPerPage : 25;
    this.includePrivateLogs = !!this.route.snapshot.data.includePrivateLogs;
    this.filters = [
      ...savedFilters,
      {
        propertyName: "eventDatetime",
        dataType: DataType.DATE,
        data: { fromDate: moment().subtract(3, "months").format("YYYY-MM-DD"), toDate: moment().endOf("day").format("YYYY-MM-DD") },
        translateKey: "page.statEvents.eventDatetime",
      },
    ];
    this.isFiltered = savedFilters.length > 0;
  }

  ngOnInit(): void {
    this.eventsDataSource = new StatEventsDataSource(this.apiStatService, this.includePrivateLogs);
    // apply filter
    this.filters?.forEach((filter) => this.eventsDataSource.setFilter(filter));
    this.activeFilters = [...this.eventsDataSource.getAllFilters()];

    // Subscribe to services setup changes
    this.sessionService.servicesSetupWatch.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.updateHealthcareServicesRefsAndLoad();
    });

    // If services are already available at init time, load data immediately
    if (this.sessionService.currentService) {
      this.updateHealthcareServicesRefsAndLoad();
    }
  }

  ngAfterViewInit(): void {
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    // on sort or paginate events, load a new page
    merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadEvents();
        })
      )
      .subscribe();

    // Detect page size change
    this.currentPageSize = this.paginator.pageSize;
    this.paginator.page.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.currentPageSize = this.paginator.pageSize;
      this.updatePreference();
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private updateHealthcareServicesRefsAndLoad(): void {
    if (this.sessionService.currentService.reference === "all") {
      // Use all available service references
      this.healthcareServicesRefs = this.organisationService.availableOrganizations()?.length
        ? this.healthcareService.availableServices().map((s) => s.asReference.reference)
        : undefined;
    } else {
      // Use only the current service reference
      this.healthcareServicesRefs = [this.sessionService.currentService?.reference];
    }

    // Load stat events
    this.loadEvents();
  }

  private loadEvents() {
    const permPath = this.includePrivateLogs ? "dashboard/statEvents/private" : "dashboard/statEvents";
    if (!this.userService.isAuthorizedSync(null, permPath, "GET")) {
      return;
    }

    this.isLoading = true;
    this.loadSubscription?.unsubscribe();

    const services = this.healthcareServicesRefs;

    this.loadSubscription = this.apiStatService
      .countStatEvents(this.getFilters(), this.sessionService.userLang, services, this.includePrivateLogs)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((count: number) => {
        this.eventsCount = count;
        this.eventsDataSource.loadData({
          sortId: this.sort.active,
          sortOrder: this.sort.direction,
          pageNumber: this.paginator.pageIndex,
          pageSize: this.paginator.pageSize,
          filters: this.getFilters(),
          lang: this.sessionService.userLang,
          services,
        });
        this.isLoading = false;
      });
  }

  public getFilter(propertyName: string): IFilter {
    return this.eventsDataSource.getFilter(propertyName);
  }

  private getFilters(): IFilter[] {
    this.filters = this.eventsDataSource.getAllFilters();
    return this.filters;
  }

  public applyFilters(filter: IFilter): void {
    this.eventsDataSource.setFilter(filter);
    this.activeFilters = [...this.eventsDataSource.getAllFilters()];
    this.loadEvents();
    this.isFiltered = this.filters.some((f) => f.propertyName !== "eventDatetime");
    this.updatePreference();
  }

  public clearFilters(): void {
    this.eventsDataSource.clearFilter();
    this.activeFilters = [];
    this.paginator.pageIndex = 0;
    this.applyDateFilter(this.currentDateInterval);
  }

  public applyDateFilter(interval: IDateInterval): void {
    if (!interval?.fromDate || !interval?.toDate) {
      return;
    }
    this.currentDateInterval = interval;
    this.applyFilters({
      propertyName: "eventDatetime",
      dataType: DataType.DATE,
      data: { fromDate: moment(interval.fromDate).format("YYYY-MM-DD"), toDate: moment(interval.toDate).format("YYYY-MM-DD") },
      translateKey: "page.statEvents.eventDatetime",
    });
  }

  public get filtersWithoutDate(): IFilter[] {
    return this.filters.filter((f) => f.propertyName !== "eventDatetime");
  }

  private updatePreference() {
    this.preferenceService
      .update({
        context: this.route.snapshot.data.ctx,
        parameters: {
          filters: this.getFilters().filter((f) => f.propertyName !== "eventDatetime"),
          itemsPerPage: this.paginator.pageSize,
        } as ITableParameter,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((parameters: IPreferenceUser) => {
        const param = parameters.preferences.find((p) => p.context === this.route.snapshot.data.ctx);
        this.preferences = param.parameters;
      });
  }
}
